import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { getFileNameByContentDisposition } from '../../helpers/file.helper';

import { Download, download } from './download';
import { SAVER, Saver } from './saver.provider';

@Injectable({ providedIn: 'root' })
export class DownloadService {
  constructor(private http: HttpClient, @Inject(SAVER) private save: Saver) {}

  /**
   * Downloads a file (after a full download), returning progress and download status during the download.
   * By default, the method for sending a request is GET, but if BODY is specified, then the POST method will be used.
   *
   * @param url Url to which the request to download the file will be sent
   * @param options.filename Downloaded file name
   * @param options.queryParams Query params
   * @param options.body Query Body. If set, a POST request will be sent
   *
   * @returns File download status and progress
   */
  download(url: string): Observable<Download>;

  download(
    url: string,
    options: {
      filename?: string;
      queryParams?: HttpParams;
      method?: 'GET';
      filenameFromContentDisposition?: false;
    }
  ): Observable<Download>;

  /**
   * Downloads a file (after a full download), returning progress and download status during download.
   * The GET method is used to send the request.
   * The file name will be taken from the Content-Disposition of the response header
   *
   * @param options.defaultFilename Downloaded file name if the Content-Disposition is empty or the filename could not be extracted from it
   * @param options.queryParams Query params
   *
   * @returns File download status and progress
   */
  download(
    url: string,
    options: {
      defaultFilename?: string;
      queryParams?: HttpParams;
      method?: 'GET';
      filenameFromContentDisposition: true;
    }
  ): Observable<Download>;

  download<T extends Object>(
    url: string,
    options: {
      filename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'POST';
      filenameFromContentDisposition?: false;
    }
  ): Observable<Download>;

  /**
   * Downloads a file (after a full download), returning progress and download status during download.
   * The POST method is used to send the request.
   * The file name will be taken from the Content-Disposition of the response header
   *
   * @param options.defaultFilename Downloaded file name if the Content-Disposition is empty or the filename could not be extracted from it
   * @param options.queryParams Query params
   * @param options.body Query Body. If set, a POST request will be sent
   *
   * @returns File download status and progress
   */
  download<T extends Object>(
    url: string,
    options: {
      defaultFilename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'POST';
      filenameFromContentDisposition: true;
    }
  ): Observable<Download>;

  download<T extends Object>(
    url: string,
    options?: {
      filename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'GET' | 'POST';
      defaultFilename?: string;
      filenameFromContentDisposition?: boolean;
    }
  ): Observable<Download> {
    (options = options || {}).method ??= options?.body ? 'POST' : 'GET';

    let downloader: Observable<HttpEvent<Blob>> | null = null;

    switch (options?.method || 'GET') {
      case 'POST':
        downloader = this.http.post(url, options.body, {
          reportProgress: true,
          observe: 'events',
          responseType: 'blob',
          params: options?.queryParams,
        });
        break;

      default:
        downloader = this.http.get(url, {
          reportProgress: true,
          observe: 'events',
          responseType: 'blob',
          params: options?.queryParams,
        });
        break;
    }
    return downloader.pipe(
      download((blob, headers) => {
        const name = options?.filenameFromContentDisposition
          ? this.getFilenameFromContentDisposition(headers, options?.defaultFilename)
          : options?.filename;

        this.save(blob, name);
      })
    );
  }

  blob(url: string): Observable<Blob>;

  blob(
    url: string,
    options: {
      filename?: string;
      queryParams?: HttpParams;
      method?: 'GET';
    }
  ): Observable<Blob>;

  blob(
    url: string,
    options: {
      defaultFilename?: string;
      queryParams?: HttpParams;
      method?: 'GET';
      filenameFromContentDisposition: true;
    }
  ): Observable<Blob>;

  blob<T extends Object>(
    url: string,
    options: {
      filename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'POST';
      filenameFromContentDisposition?: false;
    }
  ): Observable<Blob>;

  blob<T extends Object>(
    url: string,
    options: {
      defaultFilename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'POST';
      filenameFromContentDisposition: true;
    }
  ): Observable<Blob>;

  blob<T extends Object>(
    url: string,
    options?: {
      filename?: string;
      body?: T;
      queryParams?: HttpParams;
      method?: 'GET' | 'POST';
      defaultFilename?: string;
      filenameFromContentDisposition?: boolean;
    }
  ): Observable<Blob> {
    (options = options || {}).method ??= options?.body ? 'POST' : 'GET';

    let downloader: Observable<HttpResponse<Blob>> | null = null;

    switch (options?.method || 'GET') {
      case 'POST':
        downloader = this.http.post(url, options?.body, {
          observe: 'response',
          responseType: 'blob',
          params: options?.queryParams,
        });
        break;

      default:
        downloader = this.http.get(url, {
          observe: 'response',
          responseType: 'blob',
          params: options?.queryParams,
        });
        break;
    }

    return downloader.pipe(
      tap(r => {
        const name = options?.filenameFromContentDisposition
          ? this.getFilenameFromContentDisposition(r.headers, options?.defaultFilename)
          : options?.filename;

        this.save(r.body, name);
      }),
      map(r => r.body)
    );
  }

  private getFilenameFromContentDisposition(headers: HttpHeaders, defaultFilename: string): string {
    return getFileNameByContentDisposition(headers.get('Content-Disposition')) || defaultFilename;
  }
}
